import { useEffect, useState, useMemo } from "react";
import { T } from "@/components/T";
import { Link } from "react-router-dom";
import { BookMarked, Search } from "lucide-react";
import { fetchSources } from "@/lib/api";

export default function Sources() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSources().then((d) => { setList(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? list.filter((x) => `${x.title} ${x.category} ${x.note} ${x.ref}`.toLowerCase().includes(s)) : list;
  }, [q, list]);

  const groups = useMemo(() => {
    const m = {};
    filtered.forEach((x) => { (m[x.category || "Other"] = m[x.category || "Other"] || []).push(x); });
    return Object.entries(m);
  }, [filtered]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:py-14" data-testid="sources-page">
      <div className="mb-8 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--saffron)]">
          <BookMarked className="h-3.5 w-3.5" /> {list.length} <T>Sources referenced</T>
        </span>
        <h1 className="mt-4 font-serif-latin text-4xl text-ink sm:text-5xl"><T>Sources & References</T></h1>
        <p className="mx-auto mt-3 max-w-2xl text-ink-2"><T>Every katha, mantra, stotra and timing on Dharmartha is grounded in traditional śāstra. These are the scriptures and authorities our content draws upon.</T></p>
      </div>

      <div className="relative mx-auto mb-8 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sources-search" placeholder="Search sources…"
          className="w-full rounded-full border border-line bg-surface py-2.5 pl-10 pr-4 text-sm text-ink outline-none focus:border-[var(--saffron)]" />
      </div>

      {loading ? (
        <p className="text-center text-ink-3"><T>Loading…</T></p>
      ) : groups.length === 0 ? (
        <p className="text-center text-ink-3"><T>No sources match your search.</T></p>
      ) : groups.map(([cat, items]) => (
        <section key={cat} className="mb-8" data-testid={`source-group-${cat}`}>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[var(--saffron)]">{cat}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((s) => (
              <div key={s.sid} data-testid={`source-${s.sid}`} className="rounded-xl border border-line bg-surface p-4">
                <p className="font-medium text-ink">{s.title}</p>
                {s.note && <p className="mt-1 text-sm text-ink-2">{s.note}</p>}
                {s.ref && <p className="mt-2 text-xs text-ink-3"><T>Used in:</T> {s.ref}</p>}
                {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[var(--saffron)]"><T>Read more →</T></a>}
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="mt-8 text-center"><Link to="/" className="text-sm text-[var(--saffron)]"><T>← Back to home</T></Link></div>
    </div>
  );
}
